export type UserGroup = "SURELINC_ADMIN" | "CLIENT_ADMIN" | "CLIENT_READONLY" | string;


export type ManagedAwsAccountStatus = "ACTIVE" | "DISABLED";
export type ManagedAwsAccountRole = "ACCOUNT_ADMIN" | "ACCOUNT_READONLY";

export interface ManagedAwsAccount {
  clientId: string;
  organizationId?: string;
  organizationSlug?: string;
  accountId: string;
  displayName?: string;
  accountStatus: ManagedAwsAccountStatus | string;
  region?: string;
  cmmcLevel?: string;
  managementAccountId?: string;
  logArchiveAccountId?: string;
  isPrimary?: boolean;
  accountRole?: ManagedAwsAccountRole | string;
  membershipStatus?: string;
  accountConfig?: Record<string, unknown>;
  workspacesUsers?: WorkSpacesUserRecord[];
  workspaceUserCount?: number;
  createdAt?: string;
  updatedAt?: string;
  externalIdStatus?: string;
  externalIdVersion?: number;
  externalIdUpdatedAt?: string;
  clientEvidenceWorkflowEnabled?: boolean;
}

export interface ManagedAwsAccountsResponse {
  clientId: string;
  organizationId?: string;
  accounts: ManagedAwsAccount[];
  count: number;
}

export interface ManagedAwsAccountResponse {
  account: ManagedAwsAccount;
  message?: string;
}

export interface AccountMembership {
  accountId: string;
  userSub: string;
  username?: string;
  email?: string;
  accountRole: ManagedAwsAccountRole | string;
  membershipStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AccountMembershipsResponse {
  clientId: string;
  accountId: string;
  members: AccountMembership[];
}

export interface AccountMembershipResponse {
  membership: AccountMembership;
  message?: string;
}

export interface CurrentUser {
  username?: string | null;
  email?: string | null;
  groups: UserGroup[] | string;
  clientId?: string | null;
  isAdmin?: boolean;
  isClientAdmin?: boolean;
  isClientReadOnly?: boolean;
}


export interface WorkflowStateRecord {
  jobId?: string;
  requestedAction?: string;
  workflowType?: string;
  workflowGroup?: string;
  status?: string;
  updatedAt?: string;
}

export interface ClientWorkflowState {
  clientId?: string;
  accountId?: string;
  scopeKey?: string;
  approvalPending?: boolean;
  approved?: boolean;
  declined?: boolean;
  evidenceRunning?: boolean;
  deploymentRunning?: boolean;
  fullWorkflowRunning?: boolean;
  badges?: string[];
  summary?: string;
  statusReadError?: string;
  approvalPendingRecords?: WorkflowStateRecord[];
  approvedRecords?: WorkflowStateRecord[];
  declinedRecords?: WorkflowStateRecord[];
  evidenceRunningRecords?: WorkflowStateRecord[];
  deploymentRunningRecords?: WorkflowStateRecord[];
  fullWorkflowRunningRecords?: WorkflowStateRecord[];
}

export interface DeploymentStatusValue {
  status?: string;
  message?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface DeploymentProxyStatus extends DeploymentStatusValue {
  profileId: string;
  portalProxyName?: string;
  revision?: number;
  profileStatus?: string;
  pendingAction?: string;
  instanceId?: string;
  candidateInstanceId?: string;
  instanceState?: string;
  lifecycleState?: string;
  healthStatus?: string;
  assignmentStatus?: string;
  securityStatus?: string;
  securityReady?: boolean;
  securityValidatedAt?: string;
  ssmPingStatus?: string;
  protectedFromScaleIn?: boolean;
  lastAppliedAt?: string;
}

export interface DuoDirectoryControllerValue {
  hostnameOrIp?: string;
  port?: number;
}

export interface DuoDeploymentConfiguration extends DeploymentStatusValue {
  identityProtectionEnabled?: boolean;
  missingFields?: string[];
  authenticationSource?: {
    directoryId?: string;
    directoryDnsName?: string;
    domainControllers?: DuoDirectoryControllerValue[];
    baseDns?: string[];
    authenticationType?: string;
    transportType?: string;
    emailAttributes?: string[];
    duoUsernameAttribute?: string;
  };
  workspacesApplication?: {
    accountNumber?: string;
    regionId?: string;
    registrationCode?: string;
    providerName?: string;
    awsRole?: string;
    providerArn?: string;
    roleArn?: string;
  };
  roleAttributes?: {
    awsRole?: string;
    duoGroup?: string;
  };
}

export interface DeploymentOperationalStatus {
  clientId: string;
  clientName?: string;
  observedAt: string;
  overallStatus?: string;
  identityProtectionEnabled?: boolean;
  deploymentStage?: string;
  currentOperation?: string;
  currentJob?: Record<string, unknown>;
  workspacesStack?: DeploymentStatusValue;
  identityStack?: DeploymentStatusValue;
  autoScaling?: DeploymentStatusValue & {
    groupName?: string;
    minimumCapacity?: number;
    desiredCapacity?: number;
    maximumCapacity?: number;
  };
  proxies?: DeploymentProxyStatus[];
  samlMetadata?: DeploymentStatusValue & {
    verified?: boolean;
    source?: string;
    s3Uri?: string;
    sha256?: string;
  };
  workspacesSaml?: DeploymentStatusValue & {
    directoryId?: string;
    directoryStackStatus?: string;
    directoryDnsName?: string;
    domainControllerIps?: string[];
    baseDn?: string;
    registrationCode?: string;
    expectedStatus?: string;
    userAccessUrl?: string;
    relayStateParameterName?: string;
  };
  duoConfiguration?: DuoDeploymentConfiguration;
  lastSuccessfulDeployment?: DeploymentStatusValue & {
    jobId?: string;
    operation?: string;
  };
  lastValidation?: DeploymentStatusValue & {
    jobId?: string;
    executionArn?: string;
  };
  notification?: DeploymentStatusValue & {
    jobId?: string;
    recipientCount?: number;
  };
  rollback?: DeploymentStatusValue & {
    jobId?: string;
  };
  failureReason?: string;
  statusReadErrors?: string[];
}

export interface DeploymentOperationalStatusResponse {
  deploymentStatus: DeploymentOperationalStatus;
}

export interface WorkSpacesUserRecord {
  clientId?: string;
  recordType?: string;
  userName?: string;
  username?: string;
  user_name?: string;
  givenName?: string;
  given_name?: string;
  surname?: string;
  familyName?: string;
  family_name?: string;
  email?: string;
  bundleId?: string;
  bundle_id?: string;
  bundleProfile?: string;
  bundle_profile?: string;
  desktopProfile?: string;
  desktopProfileLabel?: string;
  passwordSecretArn?: string;
  password_secret_arn?: string;
  resetPasswordOnUpdate?: boolean;
  reset_password_on_update?: boolean;
  status?: string;
  lifecycleStatus?: string;
  workspaceId?: string;
  workspaceStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface ClientRecord {
  clientId: string;
  organizationId?: string;
  organizationSlug?: string;
  entityType?: string;
  primaryAccountId?: string;
  recordType?: string;
  clientName?: string;
  companyName?: string;
  clientEmail?: string;
  contactEmail?: string;
  authorizedClientEmails?: string[];
  clientEmails?: string[];
  notificationEmails?: string[];
  clientAdminEmails?: string[];
  clientReadOnlyEmails?: string[];
  portalUsers?: ClientUserRecord[];
  clientPortalUsers?: ClientUserRecord[];
  workspacesUsers?: WorkSpacesUserRecord[];
  workspaceUsers?: WorkSpacesUserRecord[];
  workSpacesUsers?: WorkSpacesUserRecord[];
  clientAccountId?: string;
  managementAccountId?: string;
  logArchiveAccountId?: string;
  region?: string;
  cmmcLevel?: string;
  clientStatus?: "ACTIVE" | "DISABLED" | string;
  status?: string;
  lifecycle?: string;
  lifecycleStatus?: string;
  statusUpdatedAt?: string;
  statusUpdatedBy?: string;
  statusReason?: string;
  workflowState?: ClientWorkflowState;
  deploymentModel?: string;
  pipelineType?: string;
  approvalModel?: string;
  pipelineName?: string;
  configSource?: string;
  contactName?: string;
  phoneNumber?: string;
  phone_number?: string;
  address?: string;
  website?: string;
  clientConfig?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface CreateClientRequest {
  email: string;
  companyName: string;
  clientId?: string;
  clientName?: string;
  contactEmail?: string;
  clientAccountId?: string;
  client_account_id?: string;
  managementAccountId?: string;
  management_account_id?: string;
  logArchiveAccountId?: string;
  log_archive_account_id?: string;
  region?: string;
  cmmcLevel?: string;
  cmmc_level?: string;
  lifecycle?: string;
  deploymentModel?: string;
  pipelineType?: string;
  approvalModel?: string;
  initialClientUserRole?: string;
  initialClientUserGroup?: string;
  notificationEmails?: string[] | string;
  notification_emails?: string[] | string;
  clientUsersManagementEnabled?: boolean;
  address?: string;
  name?: string;
  website?: string;
  phone_number?: string;
  overrides?: Record<string, unknown>;
}

export interface WelcomeEmailResult {
  status?: string;
  queued?: boolean;
  recipient?: string;
  notificationId?: string;
  idempotencyKey?: string;
  messageId?: string;
  errorCode?: string;
  reason?: string;
}

export interface CreateClientResponse {
  client: ClientRecord;
  cognitoUser?: {
    username?: string;
    userStatus?: string;
    enabled?: boolean;
  };
  welcomeEmail?: WelcomeEmailResult;
  cognitoInviteSent?: boolean;
  welcomeEmailQueued?: boolean;
  welcomeEmailSent?: boolean;
  welcomeEmailStatus?: string;
  message?: string;
}

export interface ReportJobRecord {
  clientId?: string;
  clientName?: string;
  clientAccountId?: string;
  clientRegion?: string;
  jobId?: string;
  sourceJobId?: string;
  parentJobId?: string;
  jobType?: string;
  status?: string;
  displayStatus?: string;
  lifecycleStatus?: string;
  packageLifecycleStatus?: string;
  jobStatus?: string;
  clientUploadStatus?: string;
  uploadAccessKeyStatus?: string;
  packageName?: string;
  packageId?: string;
  evidencePackageFileName?: string;
  packageType?: string;
  packageStage?: string;
  evidencePackageKey?: string;
  evidencePackageS3Uri?: string;
  finalAuditorPackageS3Uri?: string;
  finalPackageDeliveryStatus?: string;
  finalPackageDeliveryChannel?: string;
  finalAuditorPackageDownloadUrlExpiresAt?: string;
  finalReadinessPassControls?: number;
  finalReadinessPartialControls?: number;
  finalReadinessFailControls?: number;
  finalReadinessNotApplicableControls?: number;
  finalReadinessResolvedControls?: number;
  finalReadinessTotalControls?: number;
  readinessPercent?: number;
  reportType?: string;
  pipelineName?: string;
  pipelineExecutionId?: string;
  codeBuildBuildId?: string;
  cmmcLevel?: string;
  approvalStatus?: string;
  approvalRequestedAt?: string;
  approvalDecidedAt?: string;
  approvalDecidedBy?: string;
  approvalRequestedBy?: string;
  approvalUrl?: string;
  requestedAction?: string;
  preflightStatus?: string;
  preflightErrorCode?: string;
  preflightResult?: Record<string, unknown> | string;
  failureReason?: string;
  errorInfo?: Record<string, unknown> | string;
  taskToken?: string;
  expirationTime?: number | string;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  [key: string]: unknown;
}

export interface ActivityLogRecord {
  clientId?: string;
  clientName?: string;
  activityId?: string;
  eventId?: string;
  eventTimeEventId?: string;
  eventTime?: string;
  jobId?: string;
  eventType?: string;
  status?: string;
  displayStatus?: string;
  jobStatus?: string;
  packageLifecycleStatus?: string;
  clientUploadStatus?: string;
  message?: string;
  sourceUploadKey?: string;
  preservedKey?: string;
  finalOutputPrefix?: string;
  metadataSource?: string;
  uploadedSize?: number | string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface HealthResponse {
  status: string;
  service?: string;
}

export interface MeResponse extends CurrentUser {}

export interface ClientsResponse {
  clients: ClientRecord[];
  count?: number;
  matchedCount?: number;
  limit?: number;
  page?: number;
  pageCount?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
  searchMode?: "exact" | "contains" | "beginsWith" | string;
}

export interface ClientResponse {
  client: ClientRecord;
}

export interface ClientUserRecord {
  username: string;
  email?: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  customClientId?: string;
  enabled?: boolean;
  userStatus?: string;
  groups?: string[];
  groupName?: string;
  createdAt?: string;
  updatedAt?: string;
}


export interface ClientStatusUpdateRequest {
  clientStatus: "ACTIVE" | "DISABLED" | string;
  statusReason?: string;
}

export interface ClientStatusUpdateResponse {
  client: ClientRecord;
  previousStatus?: string;
  clientStatus: string;
  cognitoResults?: Array<Record<string, unknown>>;
  message?: string;
}

export interface ClientUsersResponse {
  clientId: string;
  users: ClientUserRecord[];
  count?: number;
  allowedGroups?: string[];
}

export interface ClientUsersManagementAccessResponse {
  clientId?: string;
  clientName?: string;
  enabled: boolean;
  canManage: boolean;
  featureKey?: string;
  message?: string;
}

export interface ClientUsersManagementResponse extends ClientUsersResponse {
  enabled?: boolean;
  canManage?: boolean;
  clientName?: string;
  portalUsers?: ClientUserRecord[];
  workspacesUsers?: WorkSpacesUserRecord[];
  workspaceUsers?: WorkSpacesUserRecord[];
  workspacesUserCount?: number;
  desktopProfileOptions?: WorkSpacesDesktopProfileOption[];
  message?: string;
}

export interface UpdateClientUserResponse {
  clientId: string;
  user: ClientUserRecord;
  message?: string;
}

export interface WorkSpacesUsersResponse {
  clientId: string;
  users: WorkSpacesUserRecord[];
  count?: number;
}

export interface UpsertWorkSpacesUserResponse {
  clientId: string;
  user: WorkSpacesUserRecord;
  message?: string;
}

export interface DeleteWorkSpacesUserResponse {
  clientId: string;
  deletedUserName: string;
  message?: string;
}

export interface WorkSpacesDesktopProfileOption {
  value: string;
  profile?: string;
  label: string;
  description?: string;
  computeType?: string;
  operatingSystem?: string;
  protocol?: string;
  cpu?: string;
  memory?: string;
  gpu?: string;
  storage?: string;
  rootStorageGb?: number | string;
  userStorageGb?: number | string;
}

export interface CreateClientUserRequest {
  email: string;
  groupName: string;
  name?: string;
  givenName?: string;
  familyName?: string;
}

export interface CreateClientUserResponse {
  clientId: string;
  user: ClientUserRecord;
  welcomeEmail?: WelcomeEmailResult;
  cognitoInviteSent?: boolean;
  welcomeEmailQueued?: boolean;
  welcomeEmailSent?: boolean;
  welcomeEmailStatus?: string;
  message?: string;
}

export interface DeleteClientUserResponse {
  clientId: string;
  deletedUsername: string;
  deletedEmail?: string;
  message?: string;
}

export interface JobsResponse {
  jobs: ReportJobRecord[];
  count?: number;
  matchedCount?: number;
  limit?: number;
  page?: number;
  pageCount?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
  searchMode?: "exact" | "contains" | "beginsWith" | string;
}

export interface ActivityResponse {
  activity: ActivityLogRecord[];
  count?: number;
  matchedCount?: number;
  limit?: number;
  page?: number;
  pageCount?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
  searchMode?: "exact" | "contains" | "beginsWith" | string;
}

export interface OutreachRecord {
  invitationId: string;
  company: string;
  recipientName: string;
  recipientEmail: string;
  bundleId: string;
  campaign?: string;
  notes?: string;
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
  storedStatus: "ACTIVE" | "REVOKED";
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  revokedAt?: string;
  revocationReason?: string;
  accessed: boolean;
  openCount: number;
  firstOpenedAt?: string;
  lastOpenedAt?: string;
  firstOpenedUserAgent?: string;
  lastOpenedUserAgent?: string;
  firstOpenedClientType?: "LIKELY_AUTOMATED" | "BROWSER" | "UNKNOWN" | string;
  lastOpenedClientType?: "LIKELY_AUTOMATED" | "BROWSER" | "UNKNOWN" | string;
  firstOpenedIpHash?: string;
  lastOpenedIpHash?: string;
  lastOpenedRequestId?: string;
  likelyAutomatedOpenCount: number;
  browserOpenCount: number;
  unknownOpenCount: number;
  engaged: boolean;
  engagementEventCount: number;
  lastEngagementAt?: string;
  lastEngagementType?: string;
  pageVisible10Count: number;
  pageVisible30Count: number;
  videoPlayCount: number;
  video25PercentCount: number;
  video50PercentCount: number;
  video75PercentCount: number;
  videoCompletedCount: number;
  scheduleDemoClickCount: number;
  likelyHumanEventCount?: number;
  likelyHumanHighIntentCount?: number;
}

export interface OutreachMutationResponse {
  record: OutreachRecord;
  alreadyRevoked?: boolean;
}

export interface OutreachDeleteResponse {
  ok: true;
  invitationId: string;
}

export interface OutreachPageSummary {
  records: number;
  accessed: number;
  notAccessed: number;
  totalOpens: number;
}

export interface OutreachResponse {
  items: OutreachRecord[];
  count: number;
  limit: number;
  page: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  nextToken: string | null;
  descending: true;
  sortField: "createdAt";
  summary: OutreachPageSummary;
  evaluatedCount: number;
  evaluationLimitReached: boolean;
}



export interface ClientPackageUploadPrepareRequest {
  uploadAccessKey: string;
  fileName: string;
  contentType?: string;
  fileSizeBytes?: number;
}

export interface ClientPackageUploadPrepareResponse {
  uploadUrl: string;
  method: "PUT" | string;
  bucket: string;
  key: string;
  expiresInSeconds: number;
  requiredHeaders: Record<string, string>;
  clientId: string;
  clientName?: string;
  sourceJobId?: string;
  message?: string;
}


export interface OrchestrationQueuedClient {
  clientId: string;
  accountId?: string;
  scopeKey?: string;
  clientName?: string;
  accountDisplayName?: string;
  messageId?: string;
  requestId?: string;
}

export interface OrchestrationSkippedClient {
  clientId: string;
  accountId?: string;
  scopeKey?: string;
  clientName?: string;
  accountDisplayName?: string;
  reason: string;
  requestedAction?: string;
  workflowType?: string;
  preflightErrorCode?: string;
  preflightResult?: Record<string, unknown> | string;
  activeWorkflow?: {
    clientId?: string;
    requestedGroup?: string;
    activeGroup?: string;
    activeJobId?: string;
    reason?: string;
    workflowState?: ClientWorkflowState;
  };
}

export interface RunClientsResponse {
  batchId: string;
  requestedAt?: string;
  requestedBy?: string;
  dryRun?: boolean;
  queuedCount: number;
  skippedCount: number;
  queuedClients: OrchestrationQueuedClient[];
  skippedClients: OrchestrationSkippedClient[];
  queuedAccounts?: OrchestrationQueuedClient[];
  skippedAccounts?: OrchestrationSkippedClient[];
  message?: string;
}


export interface PackageDownloadUrlResponse {
  downloadUrl: string;
  method: "GET" | string;
  expiresInSeconds: number;
  packageId: string;
  packageName?: string;
  fileName?: string;
}


export interface PackageControlRecord {
  packageId?: string;
  controlId?: string;
  clientId?: string;
  clientName?: string;
  jobId?: string;
  packageType?: string;
  packageStage?: string;
  cmmcLevel?: string;
  status?: string;
  controlFamily?: string;
  title?: string;
  assessmentObjective?: string;
  evidenceCount?: number | string;
  reasonAction?: string;
  reason?: string;
  statusReason?: string;
  recommendedAction?: string;
  actionRequiredReason?: string;
  openItems?: string[];
  finalAssessmentStatus?: string;
  reportUse?: string;
  completedEvidenceDecision?: string;
  clientDeclarationDecision?: string;
  knownInterpretation?: string;
  indexedAt?: string;
  [key: string]: unknown;
}

export interface PackageControlsResponse {
  controls: PackageControlRecord[];
  count?: number;
  clientId?: string;
  packageId?: string;
}


export type EvidenceWorkspaceStatus = string;
export type EvidenceWorkspaceDocumentStatus = string;
export type EvidenceWorkspaceUploadStatus = string;
export type EvidenceWorkspaceConversionStatus = string;
export type EvidenceWorkspaceValidationStatus = string;
export type EvidenceWorkspaceIntendedOutcome = string;
export type EvidenceWorkspaceSignedStatus = string | boolean;
export type EvidenceWorkspaceClientValidatedComplete = string | boolean;

export interface EvidenceWorkspaceSummary {
  totalDocuments?: number;
  requiredDocuments?: number;
  optionalDocuments?: number;
  uploadedDocuments?: number;
  missingRequiredDocuments?: number;
  validatedDocuments?: number;
  signedDocuments?: number;
  notApplicableDocuments?: number;
  rejectedDocuments?: number;
  warningCount?: number;
  blockingIssueCount?: number;
  readyForSubmission?: boolean;
  passCount?: number;
  partialCount?: number;
  failCount?: number;
  notApplicableCount?: number;
  totalCount?: number;
  validationErrors?: string[];
  documentValidationErrors?: Array<{
    documentId?: string;
    field?: string;
    message?: string;
  }>;
  [key: string]: unknown;
}

export interface EvidenceWorkspaceAttestation {
  clientId?: string;
  packageId?: string;
  signerName?: string;
  signerTitle?: string;
  signerEmail?: string;
  company?: string;
  accepted?: boolean;
  typedSignature?: string;
  notes?: string;
  attestationNotes?: string;
  attestedAt?: string;
  attestedBy?: string;
  [key: string]: unknown;
}

export interface EvidenceWorkspacePackage {
  clientId: string;
  packageId: string;
  jobId?: string;
  clientName?: string;
  packageName?: string;
  packageType?: string;
  packageStage?: string;
  packageStatus?: string;
  packageLifecycleStatus?: string;
  jobStatus?: string;
  status?: string;
  workspaceStatus?: EvidenceWorkspaceStatus;
  cmmcLevel?: string;
  technicalPackageId?: string;
  technicalPackageBucket?: string;
  technicalPackageKey?: string;
  finalAuditorPackageBucket?: string;
  finalAuditorPackageKey?: string;
  finalAuditorPackageS3Uri?: string;
  relatedTechnicalPackageId?: string;
  relatedTechnicalPackageName?: string;
  relatedFinalPackageId?: string;
  relatedFinalPackageName?: string;
  relatedFinalPackageStatus?: string;
  relatedFinalPackageKey?: string;
  workspaceLocked?: boolean;
  workspaceClosedReason?: string;
  workspaceClosedAt?: string;
  preparedDocumentsReady?: boolean;
  clientUploadStarted?: boolean;
  clientUploadCompleted?: boolean;
  clientAttested?: boolean;
  clientAttestedAt?: string;
  clientAttestedBy?: string;
  submissionRequestedAt?: string;
  submissionRequestedBy?: string;
  submissionRequestId?: string;
  workspacePackageBuilderProjectName?: string;
  workspacePackageBuilderBuildId?: string;
  workspaceBuildStartedAt?: string;
  workspaceBuildFailedAt?: string;
  workspaceBuildFailureReason?: string;
  workspaceBuildFailureNotificationStatus?: string;
  workspaceBuildFailureNotificationUpdatedAt?: string;
  workspaceSubmissionId?: string;
  finalPackageGeneratedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  summary?: EvidenceWorkspaceSummary;
  attestation?: EvidenceWorkspaceAttestation;
  [key: string]: unknown;
}

export interface EvidenceWorkspaceDocument {
  packageId: string;
  documentId: string;
  clientId?: string;
  jobId?: string;
  documentName?: string;
  documentType?: string;
  required?: boolean;
  documentKey?: string;
  preparedDocumentBucket?: string;
  preparedDocumentKey?: string;
  preparedDocumentS3Uri?: string;
  preparedDocumentFileName?: string;
  preparedDocumentAvailable?: boolean;
  preparedDocumentDownloadStatus?: string;
  preparedDocumentDownloadedAt?: string;
  uploadedOriginalKey?: string;
  uploadedOriginalFileName?: string;
  uploadedOriginalSha256?: string;
  uploadedOriginalContentType?: string;
  uploadedOriginalSizeBytes?: number;
  convertedPdfKey?: string;
  convertedPdfFileName?: string;
  convertedPdfSha256?: string;
  status?: EvidenceWorkspaceDocumentStatus;
  clientValidatedComplete?: EvidenceWorkspaceClientValidatedComplete;
  signed?: EvidenceWorkspaceSignedStatus;
  approvedBy?: string;
  approvalDate?: string;
  intendedOutcome?: EvidenceWorkspaceIntendedOutcome;
  allowedOutcomes?: EvidenceWorkspaceIntendedOutcome[];
  acceptedStatuses?: string[];
  controlsSupported?: string[];
  uploadStatus?: EvidenceWorkspaceUploadStatus;
  conversionStatus?: EvidenceWorkspaceConversionStatus;
  validationStatus?: EvidenceWorkspaceValidationStatus;
  attestationIncluded?: boolean;
  notes?: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
  [key: string]: unknown;
}

export interface EvidenceWorkspacePackageResponse {
  workspacePackage?: EvidenceWorkspacePackage;
  package?: EvidenceWorkspacePackage;
  summary?: EvidenceWorkspaceSummary;
  attestation?: EvidenceWorkspaceAttestation;
}

export interface EvidenceWorkspacePackagesResponse {
  workspacePackages: EvidenceWorkspacePackage[];
  count?: number;
  matchedCount?: number;
  limit?: number;
  page?: number;
  pageCount?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
}

export interface EvidenceWorkspaceDocumentsResponse {
  packageId: string;
  documents: EvidenceWorkspaceDocument[];
  summary?: EvidenceWorkspaceSummary;
  count?: number;
}

export interface PreparedDocumentDownloadRequest {
  packageId: string;
  documentId: string;
}

export interface PreparedDocumentDownloadResponse {
  downloadUrl: string;
  method: "GET" | string;
  bucket?: string;
  key?: string;
  expiresInSeconds: number;
  packageId: string;
  documentId: string;
  fileName?: string;
  contentType?: string;
}


export interface PreparedDocumentsDownloadAllResponse {
  downloadUrl: string;
  method: "GET" | string;
  bucket?: string;
  key?: string;
  expiresInSeconds: number;
  packageId: string;
  fileName?: string;
  contentType?: string;
  documentCount?: number;
}

export interface CompletedDocumentUploadRequest {
  packageId: string;
  documentId: string;
  fileName: string;
  contentType?: string;
  fileSizeBytes?: number;
  sha256?: string;
}

export interface CompletedDocumentUploadResponse {
  uploadUrl: string;
  method: "PUT" | string;
  bucket?: string;
  key: string;
  expiresInSeconds: number;
  requiredHeaders?: Record<string, string>;
  packageId: string;
  documentId: string;
  fileName?: string;
  uploadStatus?: EvidenceWorkspaceUploadStatus;
}

export interface DocumentStatusUpdateRequest {
  status?: EvidenceWorkspaceDocumentStatus;
  clientValidatedComplete?: EvidenceWorkspaceClientValidatedComplete;
  signed?: EvidenceWorkspaceSignedStatus;
  approvedBy?: string;
  approvalDate?: string;
  intendedOutcome?: EvidenceWorkspaceIntendedOutcome;
  notes?: string;
}

export interface DocumentStatusUpdateResponse {
  packageId: string;
  documentId: string;
  document: EvidenceWorkspaceDocument;
  summary?: EvidenceWorkspaceSummary;
  message?: string;
}

export interface ClientAttestationRequest {
  packageId: string;
  fullName: string;
  title?: string;
  role?: string;
  company?: string;
  email: string;
  typedSignature?: string;
  certificationAccepted: boolean;
  notes?: string;
  attestationNotes?: string;
}

export interface ClientAttestationResponse {
  packageId: string;
  clientAttested?: boolean;
  clientAttestedAt?: string;
  clientAttestedBy?: string;
  attestationDocumentKey?: string;
  attestation?: EvidenceWorkspaceAttestation;
  workspacePackage?: EvidenceWorkspacePackage;
  package?: EvidenceWorkspacePackage;
  summary?: EvidenceWorkspaceSummary;
  message?: string;
}

export interface SubmitEvidenceWorkspaceRequest {
  packageId: string;
  requestedBy?: string;
  confirmSubmit: boolean;
}

export interface SubmitEvidenceWorkspaceResponse {
  packageId: string;
  jobId?: string;
  workspaceStatus?: EvidenceWorkspaceStatus;
  finalPackageRequested?: boolean;
  validationPassed?: boolean;
  validationErrors?: string[];
  validationWarnings?: string[];
  summary?: EvidenceWorkspaceSummary;
  message?: string;
}

export interface ApiErrorBody {
  message?: string;
  error?: string;
  requestId?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body?: ApiErrorBody;

  constructor(message: string, status: number, body?: ApiErrorBody) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export interface AiChatRequest {
  clientId?: string;
  accountId?: string;
  message: string;
  sessionId?: string;
  packageId?: string;
  packageType?: string;
  controlId?: string;
  documentId?: string;
}

export interface AiSourceReference {
  sourceId?: string;
  sourceType?: string;
  location?: string;
  description?: string;
  label?: string;
  toolName?: string;
  tableName?: string;
  recordKey?: string;
  bucket?: string;
  key?: string;
  s3Uri?: string;
  relativePath?: string;
  clientId?: string;
  packageId?: string;
  jobId?: string;
  packageType?: string;
  controlId?: string;
  documentId?: string | null;
  metadata?: Record<string, string | number | boolean | null | undefined>;
  [key: string]: unknown;
}

export interface AiToolResult {
  toolName?: string;
  ok?: boolean;
  toolStatus?: string;
  status?: string;
  summary?: string;
  missing?: string[] | null;
  error?: string | null;
  sourceCount?: number;
  [key: string]: unknown;
}

export interface AiChatResponse {
  sessionId: string;
  messageId: string;
  answer: string;
  sourceReferences?: AiSourceReference[];
  toolResults?: AiToolResult[];
  metadata?: Record<string, unknown>;
}

export interface OutreachEvent {
  eventId: string;
  eventKey: string;
  invitationId: string;
  occurredAt: string;
  eventType: string;
  sessionId: string;
  visitId: string;
  userAgent: string;
  clientType: string;
  sourceIpHash: string;
  ipHashSource?: string;
  requestId: string;
  classification: string;
  classificationReason: string;
  classificationVersion: number;
  details: { assetId?: string; visibilityState?: string; webdriver?: boolean; trustedInteraction?: boolean; legacyUncorrelated?: boolean };
}

export interface OutreachEventsResponse {
  invitationId: string;
  items: OutreachEvent[];
  nextToken: string | null;
  retentionDays: number;
  coverageNote: string;
}
